import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import {
    MINIMUM_CROP_DIMENSION,
    CROP_THUMB_SW,
    CROP_THUMB_NW,
    CROP_THUMB_NE,
} from './constants';
import { addWindowListener, removeWindowListener } from '@eventbrite/eds-utils';

/**
 * Given two cropMasks, determine whether they are different
 */
export const cropMaskDidChange = (cropMask, previousCropMask) =>
    !isEqual(cropMask, previousCropMask);

/**
 * Given a two sets of props, determine if crop mask state should be updated.
 */
export const shouldUpdateCropMask = (
    { cropMask, aspectRatio, aspectRatioMaxChunkSize },
    {
        cropMask: previousCropMask,
        aspectRatio: previousAspectRatio,
        aspectRatioMaxChunkSize: previousChunkSize,
    },
) =>
    cropMaskDidChange(cropMask, previousCropMask) ||
    aspectRatio !== previousAspectRatio ||
    aspectRatioMaxChunkSize !== previousChunkSize;

/**
 * Given the natural size of an image and a crop mask,
 * convert crop mask to percentage-based numbers.
 */
export const cropMaskToPercent = (
    naturalHeight,
    naturalWidth,
    { x, y, width, height },
) => ({
    x: (x * 100) / naturalWidth,
    y: (y * 100) / naturalHeight,
    width: (width * 100) / naturalWidth,
    height: (height * 100) / naturalHeight,
});

/**
 * Given a percentage-based crop mask, format it as SVG rect attributes
 */
export const formatPercentCropMaskForSVG = ({ x, y, width, height }) => ({
    x: `${x}%`,
    y: `${y}%`,
    width: `${width}%`,
    height: `${height}%`,
});

/**
 * Given a percentage-based crop mask, format it as HTML style attributes
 */
export const formatPercentCropMaskForStyle = ({ x, y, width, height }) => ({
    left: `${x}%`,
    top: `${y}%`,
    width: `${width}%`,
    height: `${height}%`,
});

/**
 * Given a percentage-based crop mask, format it as SVG polygon points
 */
export const formatPercentCropMaskForPolygon = ({
    x: left,
    y: top,
    width,
    height,
}) => {
    const bottom = top + height;
    const right = left + width;

    return [
        '0,0',
        `${left},0`,
        `${left},${bottom}`,
        `${right},${bottom}`,
        `${right},${top}`,
        `${left},${top}`,
        `${left},0`,
        '100,0',
        '100,100',
        '0,100',
        '0,0',
    ].join(' ');
};

/**
 * Given the natural width of an image and the current width of the image, return a scale multiplier
 */
export const getScale = (currentWidth, naturalWidth) =>
    naturalWidth / currentWidth;

/**
 * Given a width, aspectRatio, and the natural size of an image, subtract width until either:
 *    A)  the desired aspect ratio is found
 *    B)  we hit a maximum acceptable reduction
 * If the correct aspect ratio is not found, the closest value in the set is returned
 */
export const correctAspectRatio = (
    widthParam,
    aspectRatio,
    naturalWidth,
    naturalHeight,
    maxAcceptableChunk = 10,
) => {
    const getDimensions = (dimWidth) => {
        const height = dimWidth / aspectRatio;

        return { height, width: dimWidth, intOffset: height % 1 };
    };
    let width = widthParam;
    const potentialDimensions = [];
    const preliminaryHeight = width / aspectRatio;

    if (preliminaryHeight > naturalHeight) {
        width = Math.floor(naturalHeight * aspectRatio);
        if (width > naturalWidth) {
            // this should only ever shrink the width because we generated a height from the width that was too tall and therefore we must LOWER the width to lower the height.
            // this function assumes the implementer has provided a valid width to start, so the width it just shrank should still be valid.
            throw new Error('Invalid crop mask dimensions detected');
        }
    }

    for (let i = 0; i <= maxAcceptableChunk; i++) {
        potentialDimensions.push(getDimensions(width - i));
        if (potentialDimensions[i].intOffset === 0) {
            return omit(potentialDimensions[i], 'intOffset');
        }
    }

    // couldn't find a perfect integer, find the one that's closest
    const sorted = potentialDimensions.sort(
        ({ intOffset: a }, { intOffset: b }) => {
            if (a < b) {
                return -1;
            }
            if (a > b) {
                return 1;
            }
            return 0;
        },
    );

    return omit(sorted[0], 'intOffset');
};

/**
 * Prevents crop mask from moving outside the acceptable image boundary
 */
export const constrainCropMaskXY = (naturalWidth, naturalHeight, cropMask) => {
    const { width, height } = cropMask;
    let { x, y } = cropMask;

    if (x + width > naturalWidth) {
        x = naturalWidth - width;
    }
    if (y + height > naturalHeight) {
        y = naturalHeight - height;
    }
    return {
        x: Math.floor(Math.max(0, x)),
        y: Math.floor(Math.max(0, y)),
        width,
        height,
    };
};

/**
 * Prevents crop mask from moving or resizing outside the acceptable image boundary
 */
export const constrainCropMask = (
    naturalWidth,
    naturalHeight,
    cropMask,
    aspectRatio,
    maxChunkSize,
) => {
    const { x, y } = cropMask;
    let { width, height } = cropMask;

    width = Math.max(MINIMUM_CROP_DIMENSION, Math.min(naturalWidth, width));
    height = Math.max(MINIMUM_CROP_DIMENSION, Math.min(naturalHeight, height));
    if (aspectRatio) {
        // http://stackoverflow.com/questions/8038605/resizing-while-maintaining-aspect-ratio-and-rounding-to-even-integers
        const corrected = correctAspectRatio(
            width,
            aspectRatio,
            naturalWidth,
            naturalHeight,
            maxChunkSize,
        );

        width = corrected.width;
        height = corrected.height;
    }
    width = Math.floor(width);
    height = Math.floor(height);

    return {
        ...constrainCropMaskXY(naturalWidth, naturalHeight, {
            x,
            y,
            width,
            height,
        }),
        width,
        height,
    };
};

/**
 * Given the size of an image and a crop mask, move crop mask to absolute center of image
 */
export const centerCropMask = (
    naturalWidth,
    naturalHeight,
    { width, height },
) => ({
    x: Math.floor((naturalWidth - width) / 2),
    y: Math.floor((naturalHeight - height) / 2),
    width,
    height,
});

/**
 * Given an image height and width, along with a percentage multiplier, return a default mask shape
 */
export const getDefaultCropMask = (
    naturalWidth,
    naturalHeight,
    aspectRatio,
    aspectRatioMaxChunkSize,
    multiplier = 0,
) => {
    const percentWidth = Math.floor(naturalWidth * multiplier);
    const percentHeight = Math.floor(naturalHeight * multiplier);
    let cropMask = {
        x: percentWidth,
        y: percentHeight,
        width: naturalWidth - percentWidth * 2,
        height: naturalHeight - percentHeight * 2,
    };

    if (aspectRatio) {
        // if aspectRatio, send through constrain to make sure aspectRatio
        // is properly enforced.  since constrainCropMask will modify the mask,
        // recenter the mask we generated.
        cropMask = centerCropMask(
            naturalWidth,
            naturalHeight,
            constrainCropMask(
                naturalWidth,
                naturalHeight,
                cropMask,
                aspectRatio,
                aspectRatioMaxChunkSize,
            ),
        );
    }

    return cropMask;
};

/**
 * Given:
 *    where drag event started
 *    where drag event is currently located
 *    the crop mask at the time the drag event started
 *    a scale multiplier
 * Determine the new position of the crop mask based on the new event position
 */
export const getNextMovedCropMask = (
    dragStartPosition,
    dragStartCrop,
    newPosition,
    scale,
    naturalWidth,
    naturalHeight,
) => {
    const { pageX: startPageX, pageY: startPageY } = dragStartPosition;
    const { pageX: newPageX, pageY: newPageY } = newPosition;
    const { x, y, width, height } = dragStartCrop;
    const unconstrainedNextCropMask = {
        x: Math.floor(x - (startPageX - newPageX) * scale),
        y: Math.floor(y - (startPageY - newPageY) * scale),
        width,
        height,
    };

    return constrainCropMaskXY(
        naturalWidth,
        naturalHeight,
        unconstrainedNextCropMask,
    );
};

/**
 * Given:
 *    on which drag-thumb the drag is occurring (direction)
 *    where a drag event started
 *    the crop mask at the time the drag event started
 *    where drag event is currently located
 *    the images natural size
 *    a scale multiplier
 *    the desired aspect ratio and related info
 * Determine the new size/shape of the crop mask based on the new event position
 */
export const getNextResizedCropMask = (
    direction,
    dragStartPosition,
    dragStartCrop,
    newPosition,
    naturalWidth,
    naturalHeight,
    scale,
    aspectRatio,
    aspectRatioMaxChunkSize,
) => {
    const { pageX: startPageX, pageY: startPageY } = dragStartPosition;
    const { pageX: newPageX, pageY: newPageY } = newPosition;
    const { x, y, width, height } = dragStartCrop;
    const invertWidth =
        direction === CROP_THUMB_SW || direction === CROP_THUMB_NW;
    const invertHeight =
        direction === CROP_THUMB_NW || direction === CROP_THUMB_NE;
    const widthAdjustment =
        (startPageX - newPageX) * scale * (invertWidth ? -1 : 1);
    const heightAdjustment =
        (startPageY - newPageY) * scale * (invertHeight ? -1 : 1);
    const adjustedWidth = Math.floor(width - widthAdjustment);
    const adjustedHeight = Math.floor(height - heightAdjustment);
    const sizeConstrainedMask = constrainCropMask(
        naturalWidth,
        naturalHeight,
        {
            x,
            y,
            width: adjustedWidth,
            height: adjustedHeight,
        },
        aspectRatio,
        aspectRatioMaxChunkSize,
    );
    const xAdjustment = invertWidth ? sizeConstrainedMask.width - width : 0;
    const yAdjustment = invertHeight ? sizeConstrainedMask.height - height : 0;

    return constrainCropMaskXY(naturalWidth, naturalHeight, {
        ...sizeConstrainedMask,
        x: x - xAdjustment,
        y: y - yAdjustment,
    });
};

/**
 * Attach listeners necessary to detect movement, normalized for mouse or touch
 */
export const addMoveListener = (listener) => {
    addWindowListener('mousemove', listener);
    addWindowListener('touchmove', listener);
};

/**
 * Remove listeners necessary to detect movement, normalized for mouse or touch
 */
export const removeMoveListener = (listener) => {
    removeWindowListener('mousemove', listener);
    removeWindowListener('touchmove', listener);
};

/**
 * Attach listeners necessary to detect action end, normalized for mouse or touch
 */
export const addActionEndListener = (listener) => {
    addWindowListener('mouseup', listener);
    addWindowListener('touchend', listener);
};

/**
 * Remove listeners necessary to detect action end, normalized for mouse or touch
 */
export const removeActionEndListener = (listener) => {
    removeWindowListener('mouseup', listener);
    removeWindowListener('touchend', listener);
};
